import ApiClient from '@afosto/api/ApiClient';

class Orders {
  constructor(client) {
    this.apiClient = client || ApiClient;
  }

  /**
   * Accept order
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description Update the order state
   * @returns {Promise}
   */
  acceptOrder(id, options = {}) {
    return this.apiClient.request({
      url: `/odr/orders/${id}/accept`,
      method: 'post',
      ...options,
    });
  }

  /**
   * Allocate order
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description allocate the order
   * @returns {Promise}
   */
  allocateOrder(id, options = {}) {
    return this.apiClient.request({
      url: `/odr/orders/${id}/allocate`,
      method: 'post',
      ...options,
    });
  }

  /**
   * Authorize order
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Request authorization for the order. Upon authorization (directly or after payment), the order will be processed automatically.
   * @returns {Promise}
   */
  authorizeOrder(id, body, options = {}) {
    return this.apiClient.request({
      url: `/odr/orders/${id}/authorize`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Cancel order
   *
   * @param {string} id
   * @param {Object} query - The optional path parameters
   * @param {string} [query.force]
   * @param {Object} options - The additional request options
   * @description Cancel the order
   * @returns {Promise}
   */
  cancelOrder(id, query = {}, options = {}) {
    const params = {
      force: query.force,
    };

    return this.apiClient.request({
      url: `/odr/orders/${id}/cancel`,
      method: 'post',
      params,
      ...options,
    });
  }

  /**
   * Confirm order
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description Update the order state
   * @returns {Promise}
   */
  confirmOrder(id, options = {}) {
    return this.apiClient.request({
      url: `/odr/orders/${id}/confirm`,
      method: 'post',
      ...options,
    });
  }

  /**
   * Calculate order
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} query - The optional path parameters
   * @param {Number} [query.rules]
   * @param {Object} options - The additional request options
   * @description Request calculation for the order
   * @returns {Promise}
   */
  createCalculation(id, body, query = {}, options = {}) {
    const params = {
      rules: query.rules,
    };

    return this.apiClient.request({
      url: `/odr/orders/${id}/calculation`,
      method: 'post',
      params,
      data: body,
      ...options,
    });
  }

  /**
   * Create order
   *
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Creates and returns the new order
   * @returns {Promise}
   */
  createStack(body, options = {}) {
    return this.apiClient.request({
      url: `/odr/orders`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Remove order
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description Removes an order
   * @returns {Promise}
   */
  deleteOrder(id, options = {}) {
    return this.apiClient.request({
      url: `/odr/orders/${id}`,
      method: 'delete',
      ...options,
    });
  }

  /**
   * Fulfil order
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description fulfil the order
   * @returns {Promise}
   */
  fulfilOrder(id, options = {}) {
    return this.apiClient.request({
      url: `/odr/orders/${id}/fulfil`,
      method: 'post',
      ...options,
    });
  }

  /**
   * Get backorders
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description Returns all backorders for current order
   * @returns {Promise}
   */
  getBackorders(id, options = {}) {
    return this.apiClient.request({
      url: `/odr/orders/${id}/backorders`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get order
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description Returns an order
   * @returns {Promise}
   */
  getOrder(id, options = {}) {
    return this.apiClient.request({
      url: `/odr/orders/${id}`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get calculation
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description Returns the newest, non expired, calculation for the order
   * @returns {Promise}
   */
  getOrderCalculation(id, options = {}) {
    return this.apiClient.request({
      url: `/odr/orders/${id}/calculation`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get projection
   *
   * @param {string} id
   * @param {Object} query - The optional path parameters
   * @param {Number} [query.pricingAt] - Unix timestamp
   * @param {Object} options - The additional request options
   * @description Returns an order&#39;s projection
   * @returns {Promise}
   */
  getOrderProjection(id, query = {}, options = {}) {
    const params = {
      pricing_at: query.pricingAt,
    };

    return this.apiClient.request({
      url: `/odr/orders/${id}/projection`,
      method: 'get',
      params,
      ...options,
    });
  }

  /**
   * Get references
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description Returns affiliated orders
   * @returns {Promise}
   */
  getOrderReferences(id, options = {}) {
    return this.apiClient.request({
      url: `/odr/orders/${id}/references`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get stacks
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description Returns all affiliated line sets that orginate from the order. For example inventory allocation, shipment or parcel data.
   * @returns {Promise}
   */
  getOrderStacks(id, options = {}) {
    return this.apiClient.request({
      url: `/odr/orders/${id}/stacks`,
      method: 'get',
      ...options,
    });
  }

  /**
   * List orders
   *
   * @param {Object} query - The optional path parameters
   * @param {string} [query.authorizationId]
   * @param {string} [query.number]
   * @param {Object} options - The additional request options
   * @description Returns a list of orders
   * @returns {Promise}
   */
  listOrders(query = {}, options = {}) {
    const params = {
      authorization_id: query.authorizationId,
      number: query.number,
    };

    return this.apiClient.request({
      url: `/odr/orders`,
      method: 'get',
      params,
      ...options,
    });
  }

  /**
   * Reference stack
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Creates and returns the new stack
   * @returns {Promise}
   */
  transitionStack(id, body, options = {}) {
    return this.apiClient.request({
      url: `/odr/orders/${id}/stacks`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Update order
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Updates order settings
   * @returns {Promise}
   */
  updateOrderSettings(id, body, options = {}) {
    return this.apiClient.request({
      url: `/odr/orders/${id}`,
      method: 'put',
      data: body,
      ...options,
    });
  }

  /**
   * Update tags
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Update tags for order
   * @returns {Promise}
   */
  updateOrderTags(id, body, options = {}) {
    return this.apiClient.request({
      url: `/odr/orders/${id}/tags`,
      method: 'put',
      data: body,
      ...options,
    });
  }

  /**
   * Update state
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Update the order state
   * @returns {Promise}
   */
  updateState(id, body, options = {}) {
    return this.apiClient.request({
      url: `/odr/orders/${id}/flow`,
      method: 'put',
      data: body,
      ...options,
    });
  }
}

export const orders = new Orders();

export default Orders;
